import { useCallback, useRef, useState } from 'react';

import {
  filesExtensions,
  MAX_FILES_SIZE,
  MB_COUNT,
} from '@app/components/DraggerComponent/data/constants';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { fileToBase64 } from '@app/utils/fileToBase64';

import type { SupportChatAttachment } from '@features/SupportChat/types';
import type { ChangeEvent } from 'react';

// Те же форматы и лимит, что у вложений в карточке лида: на стороне поддержки
// файлы показываются именно там.
const ACCEPTED_FILE_TYPES = filesExtensions
  .map((extension) => `.${extension}`)
  .join(',');

const isAllowedFile = ({ name }: File) => {
  const extension = name.split('.').pop()?.toLowerCase();

  return Boolean(extension && filesExtensions.includes(extension));
};

const createAttachmentId = (fileName: string) =>
  `attachment-${Date.now()}-${Math.random().toString(36).slice(2)}-${fileName}`;

const getTotalSize = (sizes: number[]) =>
  sizes.reduce((total, size) => total + size, 0);

export const useSupportChatAttachments = () => {
  const { showNotification } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [attachments, setAttachments] = useState<SupportChatAttachment[]>([]);
  const [isReadingFiles, setIsReadingFiles] = useState(false);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback(
    async ({ target }: ChangeEvent<HTMLInputElement>) => {
      const files = target.files ? Array.from(target.files) : [];
      // Сбрасываем, чтобы тот же файл можно было выбрать повторно
      target.value = '';

      if (files.length === 0) return;

      const allowedFiles = files.filter(isAllowedFile);

      if (allowedFiles.length < files.length) {
        showNotification(
          'Часть файлов не прикреплена: неподдерживаемый формат',
          FRONT_WARNING
        );
      }

      if (allowedFiles.length === 0) return;

      const totalSize = getTotalSize([
        ...attachments.map(({ size }) => size),
        ...allowedFiles.map(({ size }) => size),
      ]);

      if (totalSize > MAX_FILES_SIZE) {
        showNotification(
          `Суммарный размер файлов превышает ${MB_COUNT} МБ`,
          WARNING
        );

        return;
      }

      setIsReadingFiles(true);

      try {
        const nextAttachments = await Promise.all(
          allowedFiles.map(async (file) => ({
            id: createAttachmentId(file.name),
            name: file.name,
            size: file.size,
            data: await fileToBase64(file),
          }))
        );

        setAttachments((current) => [...current, ...nextAttachments]);
      } catch {
        showNotification('Не удалось прочитать файл', FRONT_WARNING);
      } finally {
        setIsReadingFiles(false);
      }
    },
    [attachments, showNotification]
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((current) =>
      current.filter((attachment) => attachment.id !== id)
    );
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    attachments,
    isReadingFiles,
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    fileInputRef,
    handleAttachClick,
    handleFilesSelected,
    handleRemoveAttachment,
    clearAttachments,
  };
};
